import { useEffect, useState } from "react";
import { http, fmtDate, formatApiError, docCategories, fileUrl, mesi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Download, FolderOpen, CheckSquare, Edit3, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const now = new Date();
const empty = () => ({ apartment_id: "", owner_id: "", category: "Altro", name: "", date: now.toISOString().slice(0, 10), expiry_date: "", notes: "", file: null });

const expiryBadge = (state) => {
  if (state === "scaduto") return <Badge className="bg-rose-100 text-rose-800 text-[10px]">SCADUTO</Badge>;
  if (state === "in_scadenza") return <Badge className="bg-amber-100 text-amber-900 text-[10px]">IN SCADENZA</Badge>;
  return null;
};

export function docState(expiry) {
  if (!expiry) return null;
  const days = Math.floor((new Date(expiry) - new Date(new Date().toISOString().slice(0, 10))) / 86400000);
  if (days < 0) return "scaduto";
  if (days <= 45) return "in_scadenza";
  return "valido";
}

export default function Documenti({ apartmentId }) {
  const [items, setItems] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [owners, setOwners] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [editId, setEditId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [taskPreview, setTaskPreview] = useState(null);
  const [fApt, setFApt] = useState(apartmentId || "all");
  const [fCat, setFCat] = useState("all");
  const [fScad, setFScad] = useState("all");

  const load = async () => {
    const p = {};
    const aptFilter = apartmentId || (fApt !== "all" && fApt !== "puravida" ? fApt : null);
    if (aptFilter) p.apartment_id = aptFilter;
    if (!apartmentId && fApt === "puravida") p.scope = "puravida";
    if (fCat !== "all") p.category = fCat;
    const { data } = await http.get("/documents", { params: p });
    setItems(data.map((d) => ({ ...d, expiry_state: docState(d.expiry_date) })));
  };
  useEffect(() => { http.get("/apartments").then((r) => setApartments(r.data)); http.get("/owners").then((r) => setOwners(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fApt, fCat, apartmentId]);

  const shown = items.filter((d) => fScad === "all" || (fScad === "scaduto" && d.expiry_state === "scaduto")
    || (fScad === "in_scadenza" && d.expiry_state === "in_scadenza") || (fScad === "con_scadenza" && !!d.expiry_date));

  const onSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      if (editId) {
        await http.put(`/documents/${editId}`, { name: form.name || null, category: form.category,
          apartment_id: (apartmentId || form.apartment_id) || null, owner_id: form.owner_id || null,
          date: form.date, expiry_date: form.expiry_date || null, notes: form.notes || null });
      } else {
        if (!form.file) { setUploading(false); return toast.error("Seleziona un file"); }
        const fd = new FormData();
        fd.append("file", form.file);
        fd.append("apartment_id", apartmentId || form.apartment_id || "");
        fd.append("owner_id", form.owner_id || "");
        fd.append("category", form.category);
        fd.append("name", form.name || "");
        fd.append("date", form.date);
        fd.append("expiry_date", form.expiry_date || "");
        fd.append("notes", form.notes || "");
        await http.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Documento salvato"); setOpen(false); setEditId(null); setForm(empty()); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };
  const onDelete = async (id) => { if (!window.confirm("Eliminare il documento?")) return; await http.delete(`/documents/${id}`); toast.success("Eliminato"); load(); };
  const openTask = async (d) => {
    const { data } = await http.get(`/documents/${d.id}/task-preview`);
    setTaskPreview({ docId: d.id, ...data });
  };
  const confirmTask = async () => {
    const p = taskPreview;
    await http.post(`/documents/${p.docId}/create-task`, { title: p.title, apartment_id: p.apartment_id, due_date: p.due_date || null, priority: p.priority, notes: p.notes, source_label: p.source_label });
    toast.success("Todo creato dalla scadenza"); setTaskPreview(null);
  };
  const aptName = (id) => apartments.find((a) => a.id === id)?.name || "PuraVida (generale)";

  return (
    <div className="space-y-6" data-testid="documenti-page">
      {!apartmentId && (
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-[var(--terracotta)] font-semibold">Documenti</div>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[var(--teal-deep)]">Documenti & scadenze</h1>
          </div>
          <Button data-testid="new-doc-btn" onClick={() => { setForm(empty()); setEditId(null); setOpen(true); }} className="bg-[var(--teal-deep)] hover:bg-[var(--teal-medium)] rounded-xl min-h-[44px]"><Plus className="h-4 w-4 mr-1" /> Carica documento</Button>
        </div>
      )}
      {apartmentId && (
        <div className="flex justify-end">
          <Button data-testid="new-doc-apt-btn" onClick={() => { setForm({ ...empty(), apartment_id: apartmentId }); setEditId(null); setOpen(true); }} className="bg-[var(--teal-deep)] hover:bg-[var(--teal-medium)] rounded-xl min-h-[44px]"><Plus className="h-4 w-4 mr-1" /> Carica documento</Button>
        </div>
      )}

      <Card className="p-4 rounded-2xl flex flex-wrap gap-3">
        {!apartmentId && (
          <div><Label>Appartamento</Label>
            <Select value={fApt} onValueChange={setFApt}><SelectTrigger className="w-[200px]" data-testid="doc-f-apt"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutti</SelectItem><SelectItem value="puravida">PuraVida (generale)</SelectItem>{apartments.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent></Select>
          </div>
        )}
        <div><Label>Categoria</Label>
          <Select value={fCat} onValueChange={setFCat}><SelectTrigger className="w-[200px]" data-testid="doc-f-cat"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutte</SelectItem>{docCategories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
        </div>
        <div><Label>Scadenza</Label>
          <Select value={fScad} onValueChange={setFScad}><SelectTrigger className="w-[180px]" data-testid="doc-f-scad"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutti</SelectItem><SelectItem value="con_scadenza">Con scadenza</SelectItem><SelectItem value="in_scadenza">In scadenza</SelectItem><SelectItem value="scaduto">Scaduti</SelectItem></SelectContent></Select>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {shown.length === 0 && <div className="col-span-full text-center text-slate-400 py-10">Nessun documento.</div>}
        {shown.map((d) => (
          <Card key={d.id} className={`rounded-2xl p-4 flex items-start gap-3 ${d.expiry_state === "scaduto" ? "border-rose-200" : d.expiry_state === "in_scadenza" ? "border-amber-200" : ""}`} data-testid={`doc-${d.id}`}>
            <div className="h-11 w-11 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center shrink-0"><FolderOpen className="h-5 w-5" /></div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm truncate">{d.name || d.filename}</div>
              <div className="text-xs text-slate-500 flex flex-wrap gap-2 mt-0.5 items-center">
                <Badge variant="outline" className="border-slate-300 text-slate-600 text-[10px]">{d.category}</Badge>
                <span>{aptName(d.apartment_id)}</span>
                <span>{fmtDate(d.date)}</span>
                {d.expiry_date && <span data-testid={`doc-expiry-${d.id}`}>scade {fmtDate(d.expiry_date)}</span>}
                {expiryBadge(d.expiry_state)}
              </div>
              {d.expiry_date && (
                <Button size="sm" variant="outline" className="rounded-lg h-8 mt-2" data-testid={`doc-create-task-${d.id}`} onClick={() => openTask(d)}>
                  <CheckSquare className="h-3.5 w-3.5 mr-1" /> Crea Todo
                </Button>
              )}
            </div>
            <div className="flex gap-1 shrink-0">
              <a href={fileUrl(d.file_id)} target="_blank" rel="noreferrer" data-testid={`doc-download-${d.id}`} className="p-2 text-slate-500 hover:text-teal-700"><Download className="h-4 w-4" /></a>
              <button onClick={() => { setForm({ ...empty(), ...d, file: null }); setEditId(d.id); setOpen(true); }} data-testid={`doc-edit-${d.id}`} className="p-2 text-slate-500 hover:text-teal-700"><Edit3 className="h-4 w-4" /></button>
              <button onClick={() => onDelete(d.id)} className="p-2 text-slate-500 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editId ? "Modifica documento" : "Carica documento"}</DialogTitle></DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="doc-form">
            <div><Label>Nome documento</Label><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="es. Assicurazione Sub 50" data-testid="doc-name" /></div>
            {!apartmentId && (
              <div><Label>Appartamento (vuoto = PuraVida generale)</Label>
                <Select value={form.apartment_id || "none"} onValueChange={(v) => setForm({ ...form, apartment_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="doc-apt"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">PuraVida (generale)</SelectItem>{apartments.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent></Select>
              </div>
            )}
            <div><Label>Proprietario (facoltativo)</Label>
              <Select value={form.owner_id || "none"} onValueChange={(v) => setForm({ ...form, owner_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="doc-owner"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">—</SelectItem>{owners.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label>Categoria</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="doc-cat"><SelectValue /></SelectTrigger>
                <SelectContent>{docCategories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Data documento</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="doc-date" /></div>
              <div><Label>Scadenza (facoltativa)</Label><Input type="date" value={form.expiry_date || ""} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} data-testid="doc-expiry" /></div>
            </div>
            {!editId && <div><Label>File (foto o PDF)</Label><Input type="file" accept="image/*,application/pdf" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} data-testid="doc-file" /></div>}
            <div><Label>Note</Label><Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button type="submit" disabled={uploading} className="bg-[var(--teal-deep)] hover:bg-[var(--teal-medium)]" data-testid="doc-submit">{uploading ? "Salvo..." : "Salva"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!taskPreview} onOpenChange={() => setTaskPreview(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="doc-task-preview">
          <DialogHeader><DialogTitle className="font-display text-2xl">Crea Todo dalla scadenza</DialogTitle></DialogHeader>
          {taskPreview && (
            <div className="space-y-3">
              <div className="rounded-xl bg-slate-50 p-3 text-xs text-slate-600">Origine: <b>{taskPreview.source_label}</b>{taskPreview.apartment_name ? ` · ${taskPreview.apartment_name}` : ""}</div>
              <div><Label>Titolo</Label><Input value={taskPreview.title} onChange={(e) => setTaskPreview({ ...taskPreview, title: e.target.value })} data-testid="doc-task-title" /></div>
              <div><Label>Scadenza</Label><Input type="date" value={taskPreview.due_date || ""} onChange={(e) => setTaskPreview({ ...taskPreview, due_date: e.target.value })} /></div>
              <div className="text-xs text-slate-500 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> Il Todo viene creato solo dopo la tua conferma.</div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTaskPreview(null)}>Annulla</Button>
            <Button onClick={confirmTask} className="bg-[var(--teal-deep)] hover:bg-[var(--teal-medium)]" data-testid="doc-task-confirm">Conferma e crea</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
